import { isValid as isValidIsoCountryCode } from 'i18n-iso-countries';
import ISO6391 from 'iso-639-1';
import { decodePathSegment } from './segment-identifier-patterns';

const NORMALIZED_LOCALE_TAG_PATTERN =
  /^[a-z]{2,3}(?:-[a-z]{4})?-(?:[a-z]{2}|\d{3})$/;
const ISO_COUNTRY_ALPHA2_PATTERN = /^[a-z]{2}$/;
const UN_M49_REGION_PATTERN = /^\d{3}$/;
const BCP47_SCRIPT_PATTERN = /^[a-z]{4}$/;

export function normalizeLocaleCatalogSegment(segment: string): string {
  return decodePathSegment(segment).trim().toLowerCase().replaceAll('_', '-');
}

function isKnownLanguageSubtag(language: string): boolean {
  return ISO6391.validate(language);
}

function isKnownRegionSubtag(region: string): boolean {
  if (UN_M49_REGION_PATTERN.test(region)) {
    return true;
  }

  return isValidIsoCountryCode(region.toUpperCase());
}

export function isKnownLocaleCatalogSegment(segment: string): boolean {
  if (!segment) {
    return false;
  }

  const normalized = normalizeLocaleCatalogSegment(segment);

  if (!NORMALIZED_LOCALE_TAG_PATTERN.test(normalized)) {
    return false;
  }

  const parts = normalized.split('-');
  const language = parts[0];

  if (!language || !isKnownLanguageSubtag(language)) {
    return false;
  }

  if (parts.length === 2) {
    const region = parts[1];

    return Boolean(region && isKnownRegionSubtag(region));
  }

  if (parts.length === 3) {
    const script = parts[1];
    const region = parts[2];

    return Boolean(
      script &&
        BCP47_SCRIPT_PATTERN.test(script) &&
        region &&
        isKnownRegionSubtag(region),
    );
  }

  return false;
}

export function isKnownIsoLanguageCodeSegment(segment: string): boolean {
  if (!segment) {
    return false;
  }

  const normalized = normalizeLocaleCatalogSegment(segment);

  return /^[a-z]{2}$/.test(normalized) && isKnownLanguageSubtag(normalized);
}

export function isKnownIsoCountryCodeSegment(segment: string): boolean {
  if (!segment) {
    return false;
  }

  const normalized = normalizeLocaleCatalogSegment(segment);

  if (!ISO_COUNTRY_ALPHA2_PATTERN.test(normalized)) {
    return false;
  }

  return isValidIsoCountryCode(normalized.toUpperCase());
}
